/**
 * Runtime client config (M7.2; FR-2.11).
 *
 * The built bundle is DNS/pool-agnostic (M5.4) — it bakes in no CloudFront domain
 * and no Cognito ids. Instead it fetches `GET /api/config` at load to learn whether
 * registered sign-in is available and, if so, the pool parameters. When Cognito
 * is not configured (local dev / a storeless task) `auth` is null and the app offers
 * guest play only. Fetched once and memoized.
 *
 * The in-app SRP auth flow (FR-2.11) needs only `region` + `userPoolId` + `clientId`;
 * `domain` (the old Hosted-UI host) is unused now but kept for one release as rollback
 * insurance (D-43.4) — `/api/config`'s shape is unchanged, so no server change.
 */

import { SERVER_HTTP_URL } from '../config';
import type { CognitoClientConfig } from '@jeopardy/client-core';

// Single-sourced with the auth core (#104): the SRP flow's Cognito parameters are
// defined once in `@jeopardy/client-core` and re-exported here for existing consumers.
export type { CognitoClientConfig } from '@jeopardy/client-core';

/**
 * CloudWatch RUM app-monitor config the client uses to initialize the RUM SDK
 * (EI-4 / #35). Absent → no RUM (local/dev/storeless server). Ingestion goes
 * browser-side to the public RUM data plane, so no VPC endpoint is required.
 */
export interface CloudWatchRumClientConfig {
  applicationId: string;
  region: string;
  identityPoolId: string;
  sessionSampleRate?: number;
}

/**
 * Backend voice availability (#93, D-CONF). When the deployment wires the Polly /
 * Transcribe adapters, `GET /api/config` advertises which of backend neural TTS
 * (`tts`) and streaming STT (`stt`) are enabled; the client factories then select the
 * backend adapters over the browser ones (D-SEAM). Absent/null (local dev / CI /
 * storeless task) → the browser speech adapters are used.
 */
export interface VoiceServiceConfig {
  tts: boolean;
  stt: boolean;
}

export interface RuntimeConfig {
  protocolVersion: number;
  /** Cognito config, or null when registered sign-in isn't configured. */
  auth: { cognito: CognitoClientConfig } | null;
  /** CloudWatch RUM config, or null when RUM isn't configured. */
  rum?: { cloudwatch: CloudWatchRumClientConfig } | null;
  /** Backend voice availability, or null when not configured (#93, D-CONF). */
  voice?: VoiceServiceConfig | null;
}

let cached: Promise<RuntimeConfig> | null = null;

/** Fetch (and memoize) the runtime config. A failure yields a guest-only config. */
export function fetchRuntimeConfig(): Promise<RuntimeConfig> {
  if (!cached) {
    cached = fetch(`${SERVER_HTTP_URL}/api/config`)
      .then((res) => {
        if (!res.ok) throw new Error(`config fetch failed (${res.status})`);
        return res.json() as Promise<RuntimeConfig>;
      })
      .catch(() => ({ protocolVersion: 0, auth: null }) satisfies RuntimeConfig);
  }
  return cached;
}

/** Test-only: drop the memoized config so each test starts clean. */
export function resetRuntimeConfigCache(): void {
  cached = null;
}
